import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import {
  FaStore,
  FaChevronDown,
  FaChevronUp,
  FaHistory,
  FaPrint
} from "react-icons/fa";
import { GiMedicines } from "react-icons/gi";
import { RiPriceTag3Line } from "react-icons/ri";
import { BsCashStack } from "react-icons/bs";
import { MdOutlineDescription } from "react-icons/md";
import {
  FiArrowLeft,
  FiBox,
  FiFileText,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiTrendingUp
} from "react-icons/fi";
import "./Print.css";

const ObatDetail = () => {
  const param = useParams();
  const flag = param["*"];
  const navigate = useNavigate();

  const [obatDetail, setObatDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOutlets, setExpandedOutlets] = useState({});

  // Modals & Drawers
  const [showStockCard, setShowStockCard] = useState(false);
  const [stockCardData, setStockCardData] = useState([]);
  const [loadingStockCard, setLoadingStockCard] = useState(false);
  const [showAllStock, setShowAllStock] = useState(false);

  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const idOutlet = parseInt(localStorage.getItem("id_outlet"), 10);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchObatDetail = async () => {
      setLoading(true);
      try {
        let newParam = encodeURI(flag);
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/drug/detail/${newParam}`;

        const response = await fetch(apiUrl, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        setObatDetail(data.data);
      } catch (err) {
        console.error(err);
        setError("Gagal mengambil detail obat.");
      } finally {
        setLoading(false);
      }
    };

    fetchObatDetail();
  }, [flag, token]);

  const toggleOutlet = (index) => {
    setExpandedOutlets((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleShowStockCard = async () => {
    if (!obatDetail?.id) return;
    setShowStockCard(true);
    setLoadingStockCard(true);

    let url = `${process.env.REACT_APP_API_BASE_URL}/v1/drug/stock-card/${obatDetail.id}`;
    if (role === "manager" && idOutlet) {
      url += `?outlet_id=${idOutlet}`;
    }

    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      setStockCardData(result.data || []);
    } catch (err) {
      console.error("Gagal mengambil kartu stok", err);
    } finally {
      setLoadingStockCard(false);
    }
  };

  const fetchOutlets = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/outlet`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const result = await res.json();
      setOutlets(result.data || []);
    } catch (error) {
      console.error("Gagal mengambil data outlet", error);
    }
  };

  const handleShowHistory = async () => {
    if (!obatDetail?.id) return;

    setShowHistory(true);
    setHistoryData([]);
    setLoadingHistory(true);

    if (role === "owner" || role === "admin" || role === "superadmin") {
      await fetchOutlets();
      return;
    }

    const currentOutlet = localStorage.getItem("id_outlet");
    if (!currentOutlet) return;

    fetchHistoryData(currentOutlet);
  };

  const fetchHistoryData = async (idOutletTarget) => {
    setLoadingHistory(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/drug/history/${obatDetail.id}/${idOutletTarget}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();

      if (Array.isArray(result.data)) {
        setHistoryData(result.data);
      } else {
        setHistoryData([]);
      }
    } catch (err) {
      console.error("Gagal mengambil history", err);
      setHistoryData([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePrintStockCard = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const dataToPrint = stockCardData || [];

    const tableRows = dataToPrint
      .map((item, index) => {
        const isEntry = item.qty > 0;
        return `
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 6px; text-align: center;">${index + 1}</td>
            <td style="border: 1px solid #cbd5e1; padding: 6px; text-align: center; font-family: monospace;">${dayjs(item.log_at).format("DD-MM-YYYY HH:mm")}</td>
            <td style="border: 1px solid #cbd5e1; padding: 6px; text-align: center; font-family: monospace; font-weight: bold;">${item.code || "-"}</td>
            <td style="border: 1px solid #cbd5e1; padding: 6px; text-align: center; color: #047857; font-weight: bold;">${isEntry ? `+${item.qty}` : "-"}</td>
            <td style="border: 1px solid #cbd5e1; padding: 6px; text-align: center; color: #dc2626; font-weight: bold;">${!isEntry ? item.qty : "-"}</td>
            <td style="border: 1px solid #cbd5e1; padding: 6px;">${item.message || "-"}</td>
          </tr>
        `;
      })
      .join("");

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kartu Stok - ${obatDetail?.name || "Obat"}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 25px; color: #1e293b; background: #ffffff; }
            .header { border-bottom: 2px solid #059669; padding-bottom: 12px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-end; }
            .header h1 { margin: 0; font-size: 22px; color: #059669; text-transform: uppercase; letter-spacing: 0.5px; }
            .header p { margin: 4px 0 0 0; font-size: 12px; color: #64748b; font-weight: 500; }
            .badge { background-color: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: bold; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-bottom: 20px; font-size: 12px; }
            .info-item { display: flex; gap: 8px; }
            .info-item .label { font-weight: bold; color: #475569; min-width: 110px; }
            .data-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 10px; }
            .data-table th { background-color: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; padding: 8px; text-align: center; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; }
            .footer { margin-top: 45px; display: flex; justify-content: space-between; font-size: 11px; text-align: center; }
            .signature-box { width: 220px; }
            .signature-space { height: 60px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>Apotek Djaya Medika</h1>
              <p>Laporan Kartu Stok Obat & Inventory Log</p>
            </div>
            <div class="badge">Dokumen Resmi Inventory</div>
          </div>

          <div class="info-grid">
            <div class="info-item"><span class="label">Nama Obat:</span><strong>${obatDetail?.name || "-"}</strong></div>
            <div class="info-item"><span class="label">Produsen:</span><span>${obatDetail?.produsen || "-"}</span></div>
            <div class="info-item"><span class="label">Dosis / Satuan:</span><span>${obatDetail?.dosis || "-"}</span></div>
            <div class="info-item"><span class="label">Tanggal Cetak:</span><span>${dayjs().format("DD MMMM YYYY, HH:mm")}</span></div>
            <div class="info-item"><span class="label">Harga Beli:</span><span>Rp ${(obatDetail?.harga_beli || 0).toLocaleString("id-ID")}</span></div>
            <div class="info-item"><span class="label">Harga Jual:</span><span>Rp ${(obatDetail?.harga_jual || 0).toLocaleString("id-ID")}</span></div>
          </div>

          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 40px;">No</th>
                <th style="width: 140px;">Tanggal & Jam</th>
                <th style="width: 130px;">Kode Ref</th>
                <th style="width: 80px;">Masuk (+)</th>
                <th style="width: 80px;">Keluar (-)</th>
                <th>Keterangan Mutasi</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows || `<tr><td colspan="6" style="text-align:center; padding: 15px;">Tidak ada data mutasi stok</td></tr>`}
            </tbody>
          </table>

          <div class="footer">
            <div class="signature-box">
              <p>Petugas Gudang / Farmasi</p>
              <div class="signature-space"></div>
              <p>( .................................... )</p>
            </div>
            <div class="signature-box">
              <p>Apoteker / Manager Outlet</p>
              <div class="signature-space"></div>
              <p>( .................................... )</p>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !obatDetail) {
    return (
      <div className="p-12 text-center text-rose-600 font-bold text-sm">
        {error || "Obat tidak ditemukan."}
      </div>
    );
  }

  const totalPages = Math.ceil(historyData.length / itemsPerPage);
  const paginatedHistory = historyData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const margin = obatDetail.harga_jual - obatDetail.harga_beli;
  const marginPercent =
    obatDetail.harga_beli > 0 ? ((margin / obatDetail.harga_beli) * 100).toFixed(1) : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/obat-paket/obat")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
              <span>{obatDetail.name}</span>
              <span className="text-xs font-normal text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 font-mono">
                {obatDetail.dosis}
              </span>
            </h1>
            <p className="text-xs text-gray-500">
              Produsen: <span className="font-bold text-gray-700">{obatDetail.produsen || "-"}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleShowHistory}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
          >
            <FaHistory className="w-3.5 h-3.5 text-teal-600" />
            <span>History Purchasing</span>
          </button>

          <button
            onClick={handleShowStockCard}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
          >
            <FiBox className="w-4 h-4" />
            <span>Kartu Stok</span>
          </button>
        </div>
      </motion.div>

      {/* Main Grid Detail */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col justify-between space-y-6"
        >
          <div className="bg-emerald-50/50 rounded-3xl p-8 flex items-center justify-center border border-emerald-100/60 aspect-square">
            <GiMedicines className="w-24 h-24 text-emerald-600" />
          </div>

          <div className="space-y-3 pt-2 text-xs">
            <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-100 flex justify-between items-center">
              <div>
                <span className="text-gray-400 font-bold uppercase text-[10px]">Harga Jual</span>
                <p className="text-lg font-extrabold text-emerald-800 font-mono">
                  Rp {(obatDetail.harga_jual || 0).toLocaleString("id-ID")}
                </p>
              </div>
              <RiPriceTag3Line className="w-6 h-6 text-emerald-600" />
            </div>

            <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-200 flex justify-between items-center">
              <div>
                <span className="text-gray-400 font-bold uppercase text-[10px]">Harga Beli HPP</span>
                <p className="text-base font-extrabold text-gray-800 font-mono">
                  Rp {(obatDetail.harga_beli || 0).toLocaleString("id-ID")}
                </p>
              </div>
              <BsCashStack className="w-6 h-6 text-gray-500" />
            </div>

            <div className="p-3 rounded-2xl bg-teal-50 border border-teal-100 flex justify-between items-center text-teal-800 font-semibold">
              <span className="flex items-center gap-1.5">
                <FiTrendingUp className="w-4 h-4 text-teal-600" /> Margin Keuntungan:
              </span>
              <span className="font-extrabold font-mono text-sm">+{marginPercent}%</span>
            </div>
          </div>
        </motion.div>

        {/* Right Column */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="md:col-span-2 bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6 flex flex-col justify-between"
        >
          <div className="space-y-5">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
                <FaStore className="text-emerald-600 w-4 h-4" />
                <span>Stok Per Outlet Penempatan</span>
              </h2>
            </div>

            <div className="space-y-3">
              {(obatDetail.outlet || []).map((lokasi, index) => {
                if (role === "manager" && lokasi.id !== idOutlet) return null;

                return (
                  <div
                    key={index}
                    className="border border-gray-100 rounded-2xl overflow-hidden bg-gray-50/50 hover:border-emerald-200 transition-colors"
                  >
                    <button
                      onClick={() => toggleOutlet(index)}
                      className="w-full flex items-center justify-between p-4 font-bold text-xs text-gray-800 hover:bg-emerald-50/30 transition-colors"
                    >
                      <span className="text-sm font-bold text-gray-900">{lokasi.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-white border border-gray-200 rounded-full font-mono text-xs font-bold text-emerald-700 shadow-sm">
                          Stok: {lokasi.stock}
                        </span>
                        {expandedOutlets[index] ? (
                          <FaChevronUp className="w-3.5 h-3.5 text-gray-400" />
                        ) : (
                          <FaChevronDown className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {expandedOutlets[index] && lokasi.exps_at?.length > 0 && (
                      <div className="p-4 bg-white border-t border-gray-100 space-y-2 text-xs">
                        <p className="font-bold text-gray-400 uppercase text-[10px]">
                          Rincian Batch Expired Date
                        </p>
                        <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                          {lokasi.exps_at.map((exp, i) => (
                            <div key={i} className="p-2.5 flex justify-between items-center text-gray-700">
                              <span className="font-mono text-gray-600 font-semibold">
                                Expired: {dayjs(exp.expired_at).format("DD MMMM YYYY")}
                              </span>
                              <span className="font-mono font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-lg border border-emerald-100">
                                {exp.stock} pcs
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-2">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
              <MdOutlineDescription className="text-emerald-600 w-4 h-4" />
              <span>Deskripsi Obat</span>
            </h3>
            <p className="text-xs text-gray-700 font-semibold bg-gray-50 p-4 rounded-2xl border border-gray-100 leading-relaxed">
              {obatDetail.desc || obatDetail.description || "Deskripsi obat tidak tersedia."}
            </p>
          </div>
        </motion.div>
      </div>

      {/* KARTU STOK MODAL */}
      <AnimatePresence>
        {showStockCard && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden border border-gray-100 flex flex-col max-h-[85vh]"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900">
                    Kartu Stok Obat
                  </h3>
                  <p className="text-xs text-emerald-700 font-semibold">
                    {obatDetail?.name} {obatDetail?.dosis}
                  </p>
                </div>
                <button
                  onClick={() => setShowStockCard(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 text-xs">
                {loadingStockCard ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                          <th className="p-3 w-10 text-center">No</th>
                          <th className="p-3">Tanggal</th>
                          <th className="p-3">Ref Code</th>
                          <th className="p-3 text-center">Masuk</th>
                          <th className="p-3 text-center">Keluar</th>
                          <th className="p-3">Keterangan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(showAllStock ? stockCardData : stockCardData.slice(0, 10)).map((item, index) => (
                          <tr key={index} className="hover:bg-emerald-50/20 transition-colors">
                            <td className="p-3 text-center font-bold text-gray-400">{index + 1}</td>
                            <td className="p-3 font-semibold text-gray-700 font-mono">
                              {dayjs(item.log_at).format("DD-MM-YYYY HH:mm")}
                            </td>
                            <td className="p-3 font-mono font-bold text-gray-800">{item.code || "-"}</td>
                            <td className="p-3 text-center font-mono font-extrabold text-emerald-700">
                              {item.qty > 0 ? `+${item.qty}` : "-"}
                            </td>
                            <td className="p-3 text-center font-mono font-extrabold text-rose-600">
                              {item.qty < 0 ? item.qty : "-"}
                            </td>
                            <td className="p-3 font-semibold text-gray-600">{item.message || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
                <button
                  onClick={() => setShowAllStock(!showAllStock)}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold transition-colors"
                >
                  {showAllStock ? "Tampilkan 10 Teratas" : "Tampilkan Semua"}
                </button>

                <button
                  onClick={handlePrintStockCard}
                  className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
                >
                  <FaPrint className="w-3.5 h-3.5" />
                  <span>Cetak Kartu Stok</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* HISTORY PURCHASING DRAWER */}
      <AnimatePresence>
        {showHistory && (
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex justify-end">
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="w-full max-w-2xl bg-white h-full shadow-2xl p-6 flex flex-col justify-between overflow-hidden"
            >
              <div className="space-y-5 flex-1 overflow-y-auto">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div className="flex items-center gap-2">
                    <FaHistory className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-lg font-bold text-gray-900">History Purchasing</h2>
                  </div>
                  <button
                    onClick={() => setShowHistory(false)}
                    className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {["owner", "admin", "superadmin"].includes(role) && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Pilih Outlet Cabang
                    </label>
                    <select
                      value={selectedOutlet || ""}
                      onChange={(e) => {
                        const selected = e.target.value;
                        setSelectedOutlet(selected);
                        fetchHistoryData(selected);
                      }}
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    >
                      <option value="">-- Pilih Outlet Cabang --</option>
                      {outlets.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {loadingHistory ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : Array.isArray(historyData) && historyData.length > 0 ? (
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm text-xs">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                          <th className="p-3 w-10 text-center">No</th>
                          <th className="p-3">Supplier (PBF)</th>
                          <th className="p-3">Harga Beli</th>
                          <th className="p-3">Kode Ref</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedHistory.map((item, index) => (
                          <tr key={index} className="hover:bg-emerald-50/20 transition-colors">
                            <td className="p-3 text-center font-bold text-gray-400">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td className="p-3 font-semibold text-gray-800">{item.name}</td>
                            <td className="p-3 font-mono font-bold text-emerald-700">
                              Rp {(item.price || 0).toLocaleString("id-ID")}
                            </td>
                            <td className="p-3 font-mono text-gray-600">{item.ref?.code || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-16 text-center text-gray-400 text-xs font-semibold">
                    Data riwayat pembelian tidak ditemukan.
                  </div>
                )}
              </div>

              {historyData.length > 0 && (
                <div className="pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                  <span className="text-gray-400">
                    Halaman {currentPage} dari {totalPages}
                  </span>

                  <div className="flex items-center gap-2">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 text-gray-700 disabled:opacity-40"
                    >
                      <FiChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 text-gray-700 disabled:opacity-40"
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ObatDetail;
